import { randomUUID } from '../../../shared/request-id.ts';
import { apiRequest } from './apiClient.js';
import { getToken, hasPermission, tryRefresh } from './auth.js';
import { createJourneyController } from './travelControllerEngine.ts';
import { readJournal, updateJournal } from './travelJournal.ts';
import { acknowledgeRecorder, clearCompletedRecorder, journeyOwner, nativeRecorderAvailable, pendingRecorder, prepareRecorder,
  readLocalJourney, recorderCapabilities, recorderCommand, replaceRecorderCommand, requestJourneyLocation } from './travelRecorder.ts';

/** Capture credentials for the current owner. A refresh may retry only while
 * that same user/org is still active; an account switch never retargets a write. */
async function request<T>(owner: string, path: string, body?: unknown, retried = false): Promise<T> {
  if (journeyOwner() !== owner) throw new Error('TRAVEL_SESSION_CONFLICT');
  const token = getToken(); if (!token) throw new Error('AUTH_REQUIRED');
  try {
    const result = await apiRequest<T>(path, { auth: false, headers: { Authorization: `Bearer ${token}` },
      ...(body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }) });
    if (journeyOwner() !== owner) throw new Error('TRAVEL_SESSION_CONFLICT');
    return result;
  } catch (error) {
    if ((error as { status?: number }).status === 401 && !retried && journeyOwner() === owner) {
      const refreshed = await tryRefresh({ throwOnTransient: true });
      if (journeyOwner() !== owner) throw new Error('TRAVEL_SESSION_CONFLICT');
      if (refreshed) return request<T>(owner, path, body, true);
    }
    throw error;
  }
}
function deviceId() {
  const storageKey = 'boss-pilot:journey-device';
  let value = localStorage.getItem(storageKey);
  if (!value) { value = randomUUID(); localStorage.setItem(storageKey, value); }
  return value;
}
const controller = createJourneyController({
  owner: journeyOwner, canWrite: () => hasPermission('costs:write'), online: () => navigator.onLine, changed: () => window.dispatchEvent(new Event('travel:changed')),
  now: () => new Date().toISOString(), deviceId, operationId: () => `journey-start:${randomUUID()}`,
  read: readJournal, update: updateJournal, request,
  uploadPoints: async (path, token, body) => {
    const response = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
    if (!response.ok) throw Object.assign(new Error('TRAVEL_UPLOAD_FAILED'), { status: response.status });
    return response.json();
  },
  nativeAvailable: nativeRecorderAvailable, capabilities: recorderCapabilities, requestLocation: requestJourneyLocation,
  local: readLocalJourney, prepare: prepareRecorder, command: recorderCommand, pending: pendingRecorder,
  replaceCommand: replaceRecorderCommand,
  acknowledge: acknowledgeRecorder, clear: clearCompletedRecorder,
});
export const { readJourneyDraft, startJourney, journeyAction, repairJourneyCommand, synchronizeJourney, finishJourney, localJourneySnapshot, recoverJourneyOwner } = controller;
